/**
 * PartnerAgreementsTab — one partner's Vehicle Schedules, and what is still outstanding.
 *
 * WHY THIS PAGE EXISTS. [D-B48] made a signed Vehicle Schedule a precondition of the driver's
 * hire purchase signature on any vehicle a third-party partner owns. Every live third-party
 * contract acquired that obligation the moment the migration applied, and the work is per PARTNER
 * — one conversation, one signing session, several vehicles — while every other view of it in this
 * app is per contract. This is the list that conversation is held against.
 *
 * TWO READS, NO NEW RPC. The denominator is `get_partner_vehicle_pipeline(p_group => 'contracts')`,
 * which `PartnerVehicleRecords.tsx:116-124` already calls; the numerator is a direct read of
 * `contract_partner_agreements` and the live documents on its arc, both admitted by the
 * `is_admin()` limb of their own SELECT policies (`20260908010000:342-343` and
 * `can_read_agreement_document`). `get_my_partner_agreements()` is the partner app's roster and is
 * scoped to `my_partner_id()`, so it refuses an admin looking at somebody else's partner; a new
 * admin-only RPC would have been a backend PR ahead of this one.
 *
 * THE SECOND READ WAITS ON THE FIRST BECAUSE IT HAS TO. Documents are keyed by arc id, and the arc
 * ids are what the referent read returns — so the two share one query rather than pretending to be
 * independent. The pipeline read runs beside them.
 *
 * ALL THE RULES LIVE IN `partnerAgreements.ts`, under test. This file draws.
 */

import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { SupabaseClient } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { ArrowRight, Building2, CheckCircle2, Clock, FileWarning, Handshake } from 'lucide-react';
import { areaPath } from '@/components/contracts/setup/steps';
import { formatBusinessDate, formatCurrency, STAGE_BADGE_STYLES, STAGE_LABELS, type VehicleStage } from '@/lib/format';
import {
    partnerAgreementRows,
    partnerAgreementsClosedNote,
    partnerAgreementsCountLine,
    partnerAgreementsSummary,
    type PartnerAgreementRow,
    type PartnerPipelineVehicle,
    type PartnerScheduleDocument,
    type PartnerScheduleReferent,
} from './partnerAgreements';

/**
 * THE UNTYPED DOOR. `contract_partner_agreements`, `agreement_documents.contract_partner_agreement_id`
 * and `get_partner_vehicle_pipeline` reach `integrations/supabase/types.ts` only when the types are
 * regenerated against PRODUCTION, which happens after the backend promote — the hatch
 * `lib/agreements/api.ts:4-7` describes. Re-pointing the same client at the untyped schema keeps
 * the query builder's own shapes instead of erasing them.
 */
const db = supabase as unknown as SupabaseClient;

interface Props {
    partnerId: number;
    /** Printed on every schedule this partner signs, and NULL until an admin types it. */
    partnerAddress: string | null;
    /** Primelane's own partner record. Its vehicles are outside the rule entirely. */
    isHouse: boolean;
}

/** One read of the pipeline. 100 is the RPC's own paging unit; the loop below covers the rest. */
const PAGE_SIZE = 100;
const MAX_PAGES = 20;

interface PipelineRead {
    rows: PartnerPipelineVehicle[];
    /** The loop hit its ceiling, so the denominator below is a floor rather than a count. */
    truncated: boolean;
}

async function fetchContractedVehicles(partnerId: number): Promise<PipelineRead> {
    const rows: PartnerPipelineVehicle[] = [];
    // THE WHOLE FLEET, NOT THE FIRST PAGE OF IT. The sentence above the table is a denominator,
    // and a denominator that silently stops at one page understates the work every time a partner
    // grows past it — which is the one thing a backfill tracker may never do.
    //
    // AND THE CEILING SAYS SO WHEN IT IS REACHED. `MAX_PAGES` is a runaway guard rather than a
    // limit anybody should meet — 2,000 vehicles, against a fleet of 289 — but a loop that simply
    // returned what it had would have made exactly the quiet understatement the paragraph above
    // forbids. The flag is what the table renders a note from.
    let truncated = false;
    for (let page = 1; page <= MAX_PAGES; page += 1) {
        const { data, error } = await db.rpc('get_partner_vehicle_pipeline', {
            p_partner_id: partnerId,
            p_page: page,
            p_page_size: PAGE_SIZE,
            p_group: 'contracts',
        });
        if (error) throw error;
        const batch = (data ?? []) as Array<PartnerPipelineVehicle & { total_count?: number }>;
        rows.push(...batch);
        if (batch.length < PAGE_SIZE) break;
        if (page === MAX_PAGES) truncated = true;
    }
    return { rows, truncated };
}

interface ScheduleReads {
    referents: PartnerScheduleReferent[];
    documents: PartnerScheduleDocument[];
}

async function fetchSchedules(partnerId: number): Promise<ScheduleReads> {
    const { data: referents, error } = await db
        .from('contract_partner_agreements')
        .select('id, contract_id, vehicle_id, signed_at, all_in_cost, management_rate')
        .eq('partner_id', partnerId);
    if (error) throw error;

    const rows = (referents ?? []) as PartnerScheduleReferent[];
    if (rows.length === 0) return { referents: [], documents: [] };

    const { data: documents, error: documentsError } = await db
        .from('agreement_documents')
        .select('contract_partner_agreement_id, status, agreement_signatures(party, status)')
        .in('contract_partner_agreement_id', rows.map((row) => row.id));
    if (documentsError) throw documentsError;

    return {
        referents: rows,
        documents: ((documents ?? []) as Array<{
            contract_partner_agreement_id: number | null;
            status: string | null;
            agreement_signatures: Array<{ party: string | null; status: string | null }> | null;
        }>).map((row) => ({
            contract_partner_agreement_id: row.contract_partner_agreement_id,
            status: row.status,
            signatures: row.agreement_signatures ?? [],
        })),
    };
}

const STATE_BADGE: Record<PartnerAgreementRow['state'], { label: string; className: string; icon: typeof CheckCircle2 }> = {
    signed: {
        label: 'Signed in app',
        className: 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-400',
        icon: CheckCircle2,
    },
    out: {
        label: 'Out for signature',
        className: 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-400',
        icon: Clock,
    },
    missing: {
        label: 'Not on file',
        className: 'border-slate-200 bg-slate-50 text-slate-600 dark:border-slate-700 dark:bg-slate-800/40 dark:text-slate-400',
        icon: FileWarning,
    },
};

/** The sentence under the badge: what is owed, by whom, in the admin's own next-step terms. */
function rowNote(row: PartnerAgreementRow): string | null {
    if (row.rateSuperseded) return 'Executed at a fee the contract no longer states — void and reissue.';
    if (row.vehicleSuperseded) return 'Executed against a vehicle this contract no longer names — void and reissue.';
    if (row.awaitingSeal) return 'Partner signed — countersign and seal.';
    if (row.state === 'out') return 'Waiting on the partner.';
    if (row.blockedByRate) return 'State the management rate before generating this document.';
    return null;
}

export default function PartnerAgreementsTab({ partnerId, partnerAddress, isHouse }: Props) {
    const navigate = useNavigate();

    const {
        data: pipeline,
        isLoading: vehiclesLoading,
        isError: vehiclesFailed,
    } = useQuery({
        queryKey: ['partner-schedule-vehicles', partnerId],
        queryFn: () => fetchContractedVehicles(partnerId),
        enabled: !!partnerId && !isHouse,
    });

    const {
        data: schedules,
        isLoading: schedulesLoading,
        isError: schedulesFailed,
    } = useQuery({
        queryKey: ['partner-schedules', partnerId],
        queryFn: () => fetchSchedules(partnerId),
        enabled: !!partnerId && !isHouse,
    });

    const rows = useMemo(
        () => partnerAgreementRows({
            vehicles: pipeline?.rows ?? [],
            referents: schedules?.referents ?? [],
            documents: schedules?.documents ?? [],
        }),
        [pipeline, schedules],
    );
    const summary = useMemo(() => partnerAgreementsSummary(rows), [rows]);

    if (isHouse) {
        return (
            <Card className="border-0">
                <CardContent className="flex items-start gap-3 py-10">
                    <Building2 className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" />
                    <div className="space-y-1">
                        <p className="font-medium">Primelane’s own fleet</p>
                        <p className="text-sm text-muted-foreground">
                            A Vehicle Schedule records what Primelane owes a third-party owner, so the
                            house partner signs none.
                        </p>
                    </div>
                </CardContent>
            </Card>
        );
    }

    const isLoading = vehiclesLoading || schedulesLoading;
    /**
     * A DEAD READ IS NOT A STATEMENT ABOUT WHAT IS SIGNED.
     *
     * Both reads go through the untyped hatch against tables the generated types do not carry yet,
     * which is the configuration most likely to fail before the promote — and with `data` undefined
     * `partnerAgreementRows` is fed empty arrays. A failed referent read then draws every row "Not
     * on file" and prints "0 of N …", and a failed pipeline read draws "None of this partner's
     * vehicles is on a contract yet". A backfill tracker reporting zero progress from a failure is
     * worse than one that shows nothing, so the failure takes the whole surface.
     */
    const readFailed = vehiclesFailed || schedulesFailed;
    const closedNote = partnerAgreementsClosedNote(summary);
    const percent = summary.total > 0 ? Math.round((summary.signed / summary.total) * 100) : 0;

    if (readFailed) {
        return (
            <Card className="border-0">
                <CardContent className="flex items-start gap-3 py-10">
                    <FileWarning className="mt-0.5 h-5 w-5 shrink-0 text-destructive" />
                    <div className="space-y-1">
                        <p className="font-medium">The schedules could not be read</p>
                        <p className="text-sm text-muted-foreground">
                            Nothing here is a statement about what this partner has signed. Reload the
                            page to try again.
                        </p>
                    </div>
                </CardContent>
            </Card>
        );
    }

    return (
        <div className="space-y-6">
            <Card className="border-0">
                <CardContent className="space-y-4 py-6">
                    <div className="flex flex-wrap items-start justify-between gap-4">
                        {/* The progress sentence replaces a skeleton once the two reads land, so it
                            is announced rather than silently swapped under a screen reader. */}
                        <div className="space-y-1" aria-live="polite">
                            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                                <Handshake className="h-3.5 w-3.5" />
                                Vehicle Schedules
                            </div>
                            {isLoading ? (
                                <Skeleton className="h-7 w-80" />
                            ) : (
                                <p className="text-lg font-medium tracking-tight">
                                    {partnerAgreementsCountLine(summary)}
                                </p>
                            )}
                            {closedNote && (
                                <p className="text-sm text-muted-foreground">{closedNote}</p>
                            )}
                            {pipeline?.truncated && (
                                <p className="text-sm text-amber-700 dark:text-amber-400">
                                    Only the first {MAX_PAGES * PAGE_SIZE} vehicles were read, so the
                                    count below is a floor rather than the whole fleet.
                                </p>
                            )}
                        </div>
                        {summary.total > 0 && (
                            <div className="w-full max-w-xs space-y-1.5">
                                <Progress value={percent} aria-label="Schedules signed" />
                                <p className="text-xs text-muted-foreground tabular-nums">
                                    {summary.outstanding === 0
                                        ? 'Nothing outstanding.'
                                        : `${summary.outstanding} outstanding`}
                                </p>
                            </div>
                        )}
                    </div>

                    {!partnerAddress?.trim() && (
                        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-900/40 dark:bg-amber-950/20 dark:text-amber-300">
                            This partner has no registered address. It prints on the schedule, so add it
                            under Manage → Edit Partner before generating one.
                        </div>
                    )}
                </CardContent>
            </Card>

            <Card className="border-0">
                <CardContent className="p-0">
                    {isLoading ? (
                        <div className="space-y-3 p-6">
                            {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-14 w-full" />)}
                        </div>
                    ) : rows.length === 0 ? (
                        <p className="py-12 text-center text-sm text-muted-foreground">
                            None of this partner’s vehicles is on a contract yet.
                        </p>
                    ) : (
                        <div className="overflow-x-auto">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Contract</TableHead>
                                        <TableHead>Vehicle</TableHead>
                                        <TableHead>Stage</TableHead>
                                        <TableHead className="text-right">Mgt rate</TableHead>
                                        <TableHead className="text-right">Cash price</TableHead>
                                        <TableHead>Schedule</TableHead>
                                        <TableHead className="w-12" />
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {rows.map((row) => {
                                        const badge = STATE_BADGE[row.state];
                                        const StateIcon = badge.icon;
                                        const note = rowNote(row);
                                        const stage = row.stage as VehicleStage | null;
                                        return (
                                            <TableRow key={row.contractId} className={row.open ? undefined : 'bg-muted/30'}>
                                                <TableCell className="font-medium">#{row.contractId}</TableCell>
                                                <TableCell>
                                                    <div className="font-medium text-sm">{row.vehicle}</div>
                                                    <div className="text-xs text-muted-foreground">
                                                        {row.registration ?? '—'}
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    <div className="flex flex-wrap items-center gap-1.5">
                                                        {stage && STAGE_LABELS[stage] ? (
                                                            <Badge variant="outline" className={STAGE_BADGE_STYLES[stage]}>
                                                                {STAGE_LABELS[stage]}
                                                            </Badge>
                                                        ) : (
                                                            <span className="text-sm text-muted-foreground">—</span>
                                                        )}
                                                        {/* SAID IN WORDS AND NOT ONLY IN OPACITY. A closed
                                                            contract was signalled by `opacity-60` alone,
                                                            which conveys nothing to a screen reader and
                                                            pushes already-muted secondary text below the
                                                            contrast floor. */}
                                                        {!row.open && (
                                                            <Badge variant="outline" className="font-normal">
                                                                Closed
                                                            </Badge>
                                                        )}
                                                    </div>
                                                </TableCell>
                                                <TableCell className="text-right text-sm tabular-nums">
                                                    {row.managementRate == null
                                                        ? <span className="text-muted-foreground">Not stated</span>
                                                        : `${parseFloat(row.managementRate.toFixed(2))}%`}
                                                </TableCell>
                                                <TableCell className="text-right text-sm tabular-nums">
                                                    {row.allInCost == null
                                                        ? <span className="text-muted-foreground">—</span>
                                                        : formatCurrency(row.allInCost)}
                                                </TableCell>
                                                <TableCell>
                                                    <Badge variant="outline" className={`gap-1 font-normal ${badge.className}`}>
                                                        <StateIcon className="h-3 w-3" />
                                                        {badge.label}
                                                    </Badge>
                                                    {row.signedAt && (
                                                        <div className="mt-1 text-xs text-muted-foreground">
                                                            {formatBusinessDate(row.signedAt)}
                                                        </div>
                                                    )}
                                                    {note && (
                                                        <div className="mt-1 max-w-[22rem] text-xs text-muted-foreground">
                                                            {note}
                                                        </div>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        className="h-8 w-8"
                                                        aria-label={`Open the agreements for contract ${row.contractId}`}
                                                        onClick={() => navigate(areaPath(row.contractId, 'agreements'))}
                                                    >
                                                        <ArrowRight className="h-4 w-4" />
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
